import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { mapSla, type CmtsSlaVm } from "../mappers/slaMappers.js";
import { repositoryCall, type RepositoryResult } from "./repositoryResult.js";
import { mapCollection } from "./repositoryCollection.js";
import { requireCmtsGuid } from "../utils/cmtsGuid.js";

export type SlaRepository = {
  profiles(tenantId?: string, activeOnly?: boolean): Promise<RepositoryResult<unknown[]>>;
  rules(profileId?: string, activeOnly?: boolean): Promise<RepositoryResult<unknown[]>>;
  trackers(tenantId?: string): Promise<RepositoryResult<readonly Record<string, unknown>[]>>;
  create(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<Record<string, unknown>>>;
  update(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<Record<string, unknown>>>;
  evaluate(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<Record<string, unknown>>>;
  pause(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<CmtsSlaVm>>;
  resume(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<CmtsSlaVm>>;
};

function dateIso(value: unknown): string | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function toTrackerRequest(payload: Record<string, unknown>, tenantId?: string): Record<string, unknown> {
  const complaintId = requireCmtsGuid(payload.complaintId, "SLA tracker complaintId");
  const ruleValue = payload.slaRuleId ?? payload.ruleId;
  return {
    tenantId,
    complaintId,
    slaRuleId: typeof ruleValue === "string" && ruleValue.trim() ? requireCmtsGuid(ruleValue, "SLA tracker ruleId") : undefined,
    startAtUtc: dateIso(payload.startAtUtc ?? payload.startDate),
    responseDueAtUtc: dateIso(payload.responseDueAtUtc ?? payload.responseDueAt),
    containmentDueAtUtc: dateIso(payload.containmentDueAtUtc ?? payload.containmentDueAt),
    resolutionDueAtUtc: dateIso(payload.resolutionDueAtUtc ?? payload.resolutionDueAt),
    verificationDueAtUtc: dateIso(payload.verificationDueAtUtc ?? payload.verificationDueAt),
    targetDateUtc: dateIso(payload.targetDateUtc ?? payload.targetDate),
    remarks: typeof payload.remarks === "string" ? payload.remarks.trim() : undefined,
  };
}

function trackerRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

export function createSlaRepository(client: CmtsHttpClient): SlaRepository {
  return {
    profiles: (tenantId, activeOnly = true) => repositoryCall(() => client.get<unknown[]>(CMTS_API.sla.profiles, { query: { tenantId, activeOnly } }), "CMTS_SLA_PROFILES"),
    rules: (profileId, activeOnly = true) => repositoryCall(() => client.get<unknown[]>(CMTS_API.sla.rules, { query: { profileId, activeOnly } }), "CMTS_SLA_RULES"),
    trackers: (tenantId) => repositoryCall(async () => mapCollection(await client.get<unknown>(CMTS_API.sla.trackers, { query: { tenantId } }), trackerRecord), "CMTS_SLA_TRACKERS"),
    create: (payload, tenantId) => repositoryCall(() => client.post<Record<string, unknown>>(CMTS_API.sla.create, toTrackerRequest(payload, tenantId), { query: { tenantId } }), "CMTS_SLA_CREATE"),
    update: (id, payload, tenantId) => repositoryCall(() => client.put<Record<string, unknown>>(`${CMTS_API.sla.create}/${encodeURIComponent(requireCmtsGuid(id, "SLA tracker id"))}`, toTrackerRequest(payload, tenantId), { query: { tenantId } }), "CMTS_SLA_UPDATE"),
    /*
     * The evaluate endpoint recalculates the tenant SLA queue. It does not
     * accept a tracker payload and returns an evaluation summary, not a
     * single SLA tracker. Sending the selected row here caused the backend
     * validation/error banner seen on the page.
     */
    evaluate: (_payload, tenantId) => repositoryCall(
      () => client.post<Record<string, unknown>>(
        CMTS_API.sla.evaluate,
        {},
        { query: { tenantId } },
      ),
      "CMTS_SLA_EVALUATE",
    ),
    pause: (id, payload, tenantId) => repositoryCall(async () => mapSla(await client.post<unknown>(CMTS_API.sla.pause(requireCmtsGuid(id, "SLA tracker id")), payload, { query: { tenantId } })), "CMTS_SLA_PAUSE"),
    resume: (id, payload, tenantId) => repositoryCall(async () => mapSla(await client.post<unknown>(CMTS_API.sla.resume(requireCmtsGuid(id, "SLA tracker id")), payload, { query: { tenantId } })), "CMTS_SLA_RESUME"),
  };
}